import {useHistory, useLocation} from 'react-router-dom'
import {useAppSelector} from 'hooks'
import {
  getListProduct,
  getPackageItem,
  getAllPackageItem,
  handleCreateProduct,
  handleUpdateProduct,
  selectListAllPackage,
  selectListPackage,
  selectListProductResponse,
  selectLoading,
  selectQuery,
  selectListProductAvailable,
  selectPurchaseData,
  getListProductAvailable,
  setPurchaseData,
  selectProductInquiry,
  PurchaseDataType,
  getListProductInquiry,
  getDetailProduct,
  getListActiveLicenses,
  getProductPurchaseHistory,
  selectListProductPurchaseHistory,
  handleGetProductHistoryOrganization,
  selectListProductPurchaseHistoryOrganization,
  selectListAvailableLicenses,
  fetchAvailableLicenses,
  selectListProductAvailableLoading,
  selectListProductInquiryLoading,
  selectListProductPaging,
  getListProductPaging,
  handleUpdateDisplayInfoProduct,
  selectIsAgreeToPurchase,
  setIsAgreeToPurchase,
} from 'features/productManagement/productManagementSlice'
import {useAppDispatch} from 'store'
import {
  AdminProductParamResponse,
  DisplayInfosType,
  GoodDisplay,
  OfferType,
  PackageListRequest,
  PricesInterface,
  ProductManagementSearchPagingRequest,
  ProductManagementSearchRequest,
  ProductPurchaseHistoryOrganizationPayload,
  ProductPurchaseHistoryPayload,
  SaleTarget,
} from 'api/productApi'
import {
  ALL_SALE_TARGET,
  TARGET_ORG_TYPE,
  TARGET_TYPE,
  TYPE_INDICATOR_PRODUCT,
  TYPE_REASON_CODE,
  TypeIndicatorProduct,
  TypeReasonCode,
  UNIT_PRICES,
  USAGE_PERIOD_UNIT,
} from 'pages/ProductsPages/constant'
import i18n from 'i18n'
import {useTranslation} from 'react-i18next'
import useOrganization from 'features/org/useOrganization'
import {formatPriceNumber} from 'helpers/uiHelper'
import {usdToCent} from 'helpers/paymentHelper'

export default function useProductManagement() {
  const history = useHistory()
  const location = useLocation()
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const loading = useAppSelector(selectLoading)
  const {organization} = useOrganization()
  const listProductAvailableLoading = useAppSelector(
    selectListProductAvailableLoading,
  )
  const listProductInquiryLoading = useAppSelector(
    selectListProductInquiryLoading,
  )
  const query = useAppSelector(selectQuery)
  const listProductResponse = useAppSelector(selectListProductResponse)
  const listPackage = useAppSelector(selectListPackage)
  const listAllPackage = useAppSelector(selectListAllPackage)
  const listProductAvailable = useAppSelector(selectListProductAvailable)
  const purchaseData = useAppSelector(selectPurchaseData)
  const productInquiry = useAppSelector(selectProductInquiry)
  const listProductPurchaseHistory = useAppSelector(
    selectListProductPurchaseHistory,
  )
  const listProductPurchaseHistoryOrganization = useAppSelector(
    selectListProductPurchaseHistoryOrganization,
  )
  const listAvailableLicenses = useAppSelector(selectListAvailableLicenses)
  const listProductPaging = useAppSelector(selectListProductPaging)
  const isAgreeToPurchase = useAppSelector(selectIsAgreeToPurchase)

  const onSearch = (payload: ProductManagementSearchRequest) => {
    try {
      const params = new URLSearchParams(
        Object.entries(payload).map(([key, value]) => [
          key,
          typeof value === 'object' ? JSON.stringify(value) : value,
        ]),
      )
      history.replace({pathname: location.pathname, search: params.toString()})
    } catch (error) {
      console.error('Update URLSearchParams failed', error)
    }
    dispatch(getListProduct(payload))
  }

  const onSearchPaging = (payload: ProductManagementSearchPagingRequest) => {
    try {
      const params = new URLSearchParams(
        Object.entries(payload).map(([key, value]) => [
          key,
          typeof value === 'object' ? JSON.stringify(value) : value,
        ]),
      )
      history.replace({pathname: location.pathname, search: params.toString()})
    } catch (error) {
      console.error('Update URLSearchParams failed', error)
    }
    dispatch(getListProductPaging(payload))
  }

  const handleGetDetailProduct = (id: string) => dispatch(getDetailProduct(id))
  const handleGetListActiveLicenses = () => dispatch(getListActiveLicenses())

  const handleGetListProductInquiry = () => {
    dispatch(getListProductInquiry())
  }

  const handleGetListProductAvailable = () =>
    dispatch(getListProductAvailable()).unwrap()

  const getPackageList = (payload: PackageListRequest) =>
    dispatch(getPackageItem(payload)).unwrap()

  const getAvailableLicenses = () => dispatch(fetchAvailableLicenses())

  const getCLS = (id: string) => {
    if (!listPackage) return ''
    const item = listPackage.filter((e) => e.itemId === id)
    if (item.length) {
      return item[0].cls
    }
    return ''
  }

  const getOrder = (id: string) => {
    if (!listPackage) return 0
    const item = listPackage.find((item) => item.itemId === id)
    if (item) return item.order
    return 0
  }

  const getPackageName = (id: string) => {
    if (!listPackage) return ''
    const item = listPackage.filter((e) => e.itemId === id)
    return (
      item[0]?.displayInfos?.find(
        (info) => info.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
      )?.name || ''
    )
  }

  const onCreate = (payload: AdminProductParamResponse) =>
    dispatch(handleCreateProduct(payload)).unwrap()

  const onUpdate = (payload: {
    data: AdminProductParamResponse
    goodsId: string
  }) => dispatch(handleUpdateProduct(payload)).unwrap()

  const onUpdateDisplayInfo = (payload: {
    displayInfos: DisplayInfosType[]
    goodsId: string
  }) => dispatch(handleUpdateDisplayInfoProduct(payload)).unwrap()

  const getAllPackageList = () => dispatch(getAllPackageItem())

  function isEqualSaleTarget(a: SaleTarget, b: SaleTarget): boolean {
    if (a.targetOp !== b.targetOp) return false
    if (a.targets.length !== b.targets.length) return false

    const sortFn = (t: {targetType: string; targetId: string}) =>
      `${t.targetType}:${t.targetId}`
    const aSorted = [...a.targets].sort((x, y) =>
      sortFn(x).localeCompare(sortFn(y)),
    )
    const bSorted = [...b.targets].sort((x, y) =>
      sortFn(x).localeCompare(sortFn(y)),
    )

    return aSorted.every(
      (t, i) =>
        t.targetType === bSorted[i].targetType &&
        t.targetId === bSorted[i].targetId,
    )
  }

  const getIndicatorType = (saleTarget: SaleTarget): TypeIndicatorProduct => {
    const orgType = saleTarget.targets.find(
      (item) => item.targetType === TARGET_TYPE.ORG_TYPE,
    )
    if (!orgType || isEqualSaleTarget(saleTarget, ALL_SALE_TARGET)) {
      return TYPE_INDICATOR_PRODUCT.ALL
    }
    if (orgType.targetId === TARGET_ORG_TYPE.HEALTH) {
      return TYPE_INDICATOR_PRODUCT.HEALTH
    }
    if (orgType.targetId === TARGET_ORG_TYPE.HOSPITAL) {
      return TYPE_INDICATOR_PRODUCT.HOSPITAL
    }
    return TYPE_INDICATOR_PRODUCT.ALL
  }

  const onSetPurchaseData = (data: PurchaseDataType) => {
    dispatch(setPurchaseData(data))
  }

  const onSetIsAgreeToPurchase = (value: boolean) => {
    dispatch(setIsAgreeToPurchase(value))
  }

  const handleGetProductPurchaseHistory = (
    payload: ProductPurchaseHistoryPayload,
  ) => {
    try {
      const params = new URLSearchParams(
        Object.entries(payload).map(([key, value]) => [
          key,
          typeof value === 'object' ? JSON.stringify(value) : value,
        ]),
      )
      history.replace({pathname: location.pathname, search: params.toString()})
    } catch (error) {
      console.error('Update URLSearchParams failed', error)
    }
    dispatch(getProductPurchaseHistory(payload))
  }

  const handleGetProductPurchaseOrganizationHistory = (
    payload: ProductPurchaseHistoryOrganizationPayload,
  ) => {
    try {
      const params = new URLSearchParams(
        Object.entries(payload).map(([key, value]) => [
          key,
          typeof value === 'object' ? JSON.stringify(value) : value,
        ]),
      )
      history.replace({pathname: location.pathname, search: params.toString()})
    } catch (error) {
      console.error('Update URLSearchParams failed', error)
    }
    dispatch(handleGetProductHistoryOrganization(payload))
  }

  const getNameProduct = (goodsName: GoodDisplay[]) => {
    const lang = i18n.language === 'ko' ? 'ko' : 'en'
    const item = goodsName.find((item) => item.langCode === lang)

    return {
      name: item?.name ?? '',
      description: item?.description,
    }
  }

  const getPriceProduct = (salePrice: PricesInterface[]) => {
    const currency =
      organization?.country.code === 'KR'
        ? UNIT_PRICES.KRW.toString()
        : UNIT_PRICES.USD.toString()

    return `${currency} ${formatPriceNumber(
      currency === UNIT_PRICES.KRW.toString()
        ? salePrice.find((item) => item.currency === UNIT_PRICES.KRW)?.price ??
            0
        : usdToCent(
            salePrice.find((item) => item.currency === UNIT_PRICES.USD)
              ?.price ?? 0,
          ),
    )}`
  }

  const getPeriodProduct = (offer: OfferType) => {
    return offer.usagePeriodUnit === USAGE_PERIOD_UNIT.LIFETIME
      ? t('IUnlimited')
      : `${offer.usagePeriod || ''} ${t('IMonths')}`
  }

  const getQuantityLabel = (quantity: number) => {
    if (quantity > 10000000) {
      return t('IUnlimited')
    }
    return formatPriceNumber(quantity)
  }

  const getReasonCodeLabel = (reasonCode: TypeReasonCode) => {
    switch (reasonCode) {
      case TYPE_REASON_CODE.PAYMENT_APPROVAL:
        return t('IApprovalPayment')
      case TYPE_REASON_CODE.ASSIGN_BY_ADMIN:
        return t('IAssignedByAdmin')
      case TYPE_REASON_CODE.COMPENSATION:
        return t('ICompensationPayment')
      case TYPE_REASON_CODE.PAYMENT_REFUND:
        return t('IRefundPayment')
      case TYPE_REASON_CODE.PAYMENT_FREE:
        return t('IFreePayment')
      case TYPE_REASON_CODE.ADJUSTMENT:
        return t('IAnalysisVolumeControl')
      case TYPE_REASON_CODE.OTHER:
        return t('IOther')
      default:
        return '-'
    }
  }

  return {
    loading,
    listProductAvailableLoading,
    listProductInquiryLoading,
    query,
    listProductResponse,
    listPackage,
    listAllPackage,
    listProductAvailable,
    listProductPaging,
    purchaseData,
    productInquiry,
    listProductPurchaseHistory,
    listProductPurchaseHistoryOrganization,
    onSearch,
    onSearchPaging,
    getPackageList,
    onCreate,
    onUpdate,
    onUpdateDisplayInfo,
    getAllPackageList,
    getIndicatorType,
    handleGetListProductAvailable,
    onSetPurchaseData,
    getPackageName,
    getCLS,
    getOrder,
    handleGetListProductInquiry,
    handleGetDetailProduct,
    handleGetListActiveLicenses,
    handleGetProductPurchaseHistory,
    handleGetProductPurchaseOrganizationHistory,
    getNameProduct,
    getPriceProduct,
    getReasonCodeLabel,
    isEqualSaleTarget,
    listAvailableLicenses,
    getAvailableLicenses,
    getPeriodProduct,
    getQuantityLabel,
    isAgreeToPurchase,
    onSetIsAgreeToPurchase,
  }
}
